/**
 * @file ImportSpreadsheetModal.tsx
 * @description A modal component for handling spreadsheet import functionality.
 * This component manages file upload, data preview, and column mapping for imported spreadsheets.
 */

import React, { useState } from "react";
import { read, utils } from "xlsx";
import ColumnMapper from "./ColumnMapper";
import { ContentDetails } from "../../types";
import { generateColumnMappings } from "../utilities/spreadsheetUtils";

interface ImportSpreadsheetModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImportConfirm: (mappedData: ContentDetails[]) => void;
  columnDefs: any[];
}

const ImportSpreadsheetModal: React.FC<ImportSpreadsheetModalProps> = ({
  isOpen,
  onClose,
  onImportConfirm,
  columnDefs,
}) => {
  const [importedData, setImportedData] = useState<any[]>([]);
  const [mappedColumns, setMappedColumns] = useState<Record<string, string>>(
    {}
  );
  const [showColumnMapper, setShowColumnMapper] = useState(false);

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      try {
        const data = await file.arrayBuffer();
        const workbook = read(data);
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = utils.sheet_to_json(worksheet, { header: 1 });
        setImportedData(jsonData);

        if (jsonData.length > 0) {
          const sheetColumns = jsonData[0] as string[];
          const appColumns = columnDefs
            .map((def) => def.field)
            .filter((field) => field !== "status" && field !== "website" && field !== "clientInfo");
          const autoMappings = generateColumnMappings(sheetColumns, appColumns);
          setMappedColumns(autoMappings);

          const necessaryColumns = appColumns.filter((col) => col !== "status" && col !== "website" && col !== "clientInfo");
          const allNecessaryColumnsMapped = necessaryColumns.every((col) =>
            Object.values(autoMappings).includes(col)
          );
          setShowColumnMapper(!allNecessaryColumnsMapped);
        }
      } catch (error) {
        console.error("Error reading spreadsheet", error);
        // TODO: Add error handling, possibly using toast
      }
    }
  };

  const handleConfirm = () => {
    const mappedData = importedData.slice(1).map((row: any[]) => {
      const mappedRow: Partial<ContentDetails> = {};
      Object.entries(mappedColumns).forEach(([sheetColumn, appColumn]) => {
        const columnIndex = importedData[0].indexOf(sheetColumn);
        if (columnIndex !== -1) {
          mappedRow[appColumn as keyof ContentDetails] = row[columnIndex];
        }
      });
      return mappedRow as ContentDetails;
    });
    onImportConfirm(mappedData);
    onClose();
  };

  const handleDownloadTemplate = () => {
    const link = document.createElement("a");
    link.href =
      "/wp-content/themes/fps22/assets/ai-seo-writer/plan-template.xlsx";
    link.download = "Piano editoriale.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <dialog className={`!flex modal ${isOpen ? "modal-open" : ""}`}>
      <div className="modal-box [--rounded-btn:9999px] flex flex-col gap-4 text-lg text-center items-center bg-white bg-none">
        <h3 className="!mb-4 !text-4xl !font-normal normal-case text-primary">
          Importa file Excel
        </h3>
        <p>
          Puoi importare da un file Excel (.xlsx, .xls, .csv) i dettagli
          relativi ai contenti da generare. Scarica un{" "}
          <button
            onClick={handleDownloadTemplate}
            className="text-primary hover:underline focus:outline-none"
          >
            template
          </button>
          .
        </p>
        <input
          type="file"
          accept=".xlsx,.xls,.csv"
          onChange={handleFileUpload}
          className="w-full max-w-xs mx-auto my-1 !normal-case file-input file-input-bordered file-input-primary"
        />
        {importedData.length > 0 && showColumnMapper && (
          <ColumnMapper
            sheetColumns={importedData[0]}
            appColumns={columnDefs.map((def) => def.field).filter((field) => field !== "status" && field !== "website" && field !== "clientInfo")}
            onColumnMapped={setMappedColumns}
            initialMappings={mappedColumns}
          />
        )}
        <div className="modal-action">
          <button
            className="px-6 my-1 text-base font-medium btn btn-outline flex-nowrap whitespace-nowrap btn-primary"
            onClick={onClose}
          >
            Annulla
          </button>
          <button
            className="px-6 my-1 text-base font-medium btn btn-primary"
            onClick={handleConfirm}
            disabled={importedData.length === 0}
          >
            Importa
          </button>
        </div>
      </div>
      <form method="dialog" className="modal-backdrop">
        <button onClick={onClose}>close</button>
      </form>
    </dialog>
  );
};

export default ImportSpreadsheetModal;
